/**
 * Client360 Overview 2.0 (Этап 1) — uk labels + the full-₴ formatter.
 *
 * record_type / business_model labels mirror the backend i18n
 * (Resources/i18n/uk_UA/CClientHolding.json + CClientBusinessProfile.json) so the
 * header pills and the classification drawer read identically to the data layer.
 */
export const RECORD_TYPE_LABEL: Record<string, string> = {
  real_b2b_client: "Клієнт (компанія / ФОП)",
  breeder_person: "Заводчик (фіз. особа)",
  technical_account: "Технічний запис",
  event_exhibition_account: "Виставка / захід",
  internal_account: "Внутрішній запис",
  retail_b2c_customer: "Роздрібний покупець",
  unknown: "Не визначено",
};

/** Short pill label (compact header chip). */
export const RECORD_TYPE_PILL: Record<string, string> = {
  real_b2b_client: "клієнт",
  breeder_person: "заводчик",
  technical_account: "технічний",
  event_exhibition_account: "виставка",
  internal_account: "внутрішній",
  retail_b2c_customer: "роздріб",
  unknown: "не визначено",
};

/** Holding additional-activities axis (CClientHolding.cCAdditionalActivities). */
export const ADDITIONAL_ACTIVITY_LABEL: Record<string, string> = {
  grooming: "Грумінг",
  vet_services: "Ветпослуги",
  online_sales: "Онлайн-продажі",
  wholesale: "Опт",
  retail_points: "Роздрібні точки",
  breeding: "Розведення",
  marketplace_sales: "Маркетплейс-продажі",
  training_events: "Навчання / заходи",
  other: "Інше",
};

/** Legal-identity axis (Account grain) — read-only label in the classification drawer. */
export const LEGAL_IDENTITY_LABEL: Record<string, string> = {
  organization: "Організація",
  fop: "ФОП",
  individual: "Фізична особа",
};

/**
 * §3 LegalContour IA v1.1 — the «Юрособи» surface/tab label is type-aware
 * (Account/1С not changed, only presentation). individual-only → «Дані особи»
 * (NOT «Юрособа»); fop → «ФОП / Реквізити»; single org → «Юрособа / Реквізити»
 * (compact); multi-LE holding → «Юрособи / Реквізити».
 */
export function legalSurfaceLabel(
  legalIdentityType: string,
  activeMembers: number,
): string {
  if (activeMembers > 1) return "Юрособи / Реквізити";
  switch (legalIdentityType) {
    case "individual":
      return "Дані особи";
    case "fop":
      return "ФОП / Реквізити";
    case "organization":
      return "Юрособа / Реквізити";
    default:
      return "Юрособи";
  }
}

/**
 * §3b — «Точки» relevance is decided by client_record_type + business_model,
 * NOT by legalIdentityType. ФОП-магазин має точки; ФОП-заводчик / фізособа — ні.
 */
export function outletsRelevant(
  recordType: string | null | undefined,
  primaryBusinessModel: string | null | undefined,
  legalIdentityType: string | null | undefined,
): boolean {
  if (legalIdentityType === "individual") return false; // фізособа — ні
  if (recordType === "breeder_person") return false; // заводчик-запис — ні
  if (primaryBusinessModel === "breeder_professional") return false; // ФОП-заводчик — ні
  return true; // pet_shop / chain / організація / тощо — релевантні
}

export const BUSINESS_MODEL_LABEL: Record<string, string> = {
  independent_pet_shop: "Незалежний зоомагазин",
  retail_chain: "Роздрібна мережа",
  vet_clinic: "Ветклініка",
  grooming_salon: "Грумінг-салон",
  breeder_professional: "Професійний заводчик",
  online_store: "Інтернет-магазин",
  marketplace_seller: "Маркетплейс-продавець",
  wholesale_reseller: "Оптовий реселер",
  dealer_partner: "Дилер / партнер",
  purchasing_group: "Закупівельна група",
  shelter_ngo: "Притулок / ГО",
  trainer_kennel_club: "Кінолог / клуб",
  farm_agro_pet_point: "Ферма / агро зоо-точка",
  other: "Інше",
};

/** Full grouped ₴ (space thousands, no decimals): 186240 → "186 240 ₴". null → "—". */
export function uahFull(v: number | null | undefined): string {
  if (v == null) return "—";
  return new Intl.NumberFormat("uk-UA", { maximumFractionDigits: 0 }).format(v) + " ₴";
}

/** DD.MM.YYYY from ISO / Y-m-d (with optional time). null → "—". */
export function dmy(v: string | null | undefined): string {
  if (!v) return "—";
  const iso = v.length <= 10 ? v + "T00:00:00" : v.replace(" ", "T");
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Activity-type label + dot color token (recent-activity timeline). */
export const ACTIVITY_TYPE_LABEL: Record<string, string> = {
  meeting: "Зустріч",
  call: "Дзвінок",
  task: "Задача",
  note: "Нотатка",
};
